import './CustomerFeedbackPopup.css';
import React, { useRef, useEffect, useCallback } from 'react';
import { tTeamMember } from '../data/Team';

export type tVelocityRecord = {
  sprint: number;
  totalVelocity: number;
  developerVelocity: { [developerId: number]: { code: number; qa: number; name: string; icon: string } };
};

interface VelocityReportPopupProps {
  velocityHistory: tVelocityRecord[];
  team: tTeamMember[];
  closeVelocityReportCallback: () => void;
}

export default function VelocityReportPopup(props: VelocityReportPopupProps) {
  const chartRef = useRef<HTMLCanvasElement | null>(null);

  // Collect all unique developers who have ever worked across all sprints
  const allDevelopers = React.useMemo(() => {
    const devMap = new Map<number, { name: string; icon: string }>();
    props.velocityHistory.forEach(record => {
      Object.entries(record.developerVelocity).forEach(([devId, devData]) => {
        if (!devMap.has(Number(devId))) {
          devMap.set(Number(devId), { name: devData.name, icon: devData.icon });
        }
      });
    });
    // Sort by developer ID to maintain consistent order
    return Array.from(devMap.entries()).sort((a, b) => a[0] - b[0]);
  }, [props.velocityHistory]);

  const drawChart = useCallback(() => {
    const canvas = chartRef.current;
    if (canvas && props.velocityHistory.length > 0) {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        const chartWidth = canvas.width;
        const chartHeight = 200;
        const padding = 40;
        const barWidth = 40;
        const barSpacing = 20;

        // Clear previous chart
        ctx.clearRect(0, 0, chartWidth, chartHeight);

        // Find the maximum velocity for scaling
        const maxVelocity = Math.max(...props.velocityHistory.map(v => v.totalVelocity), 1);
        const minVelocity = Math.min(...props.velocityHistory.map(v => v.totalVelocity));

        // Draw bars for each sprint
        props.velocityHistory.forEach((record, index) => {
          const x = index * (barWidth + barSpacing) + padding;
          const height = (record.totalVelocity / maxVelocity) * (chartHeight - padding * 2);
          const y = chartHeight - height - padding;

          // Color the bars based on the value
          if (record.totalVelocity === minVelocity && props.velocityHistory.length > 1) {
            ctx.fillStyle = 'red'; // Lowest bar
          } else if (record.totalVelocity === maxVelocity) {
            ctx.fillStyle = 'green'; // Highest bar
          } else {
            ctx.fillStyle = '#36a2eb'; // Other bars
          }

          ctx.fillRect(x, y, barWidth, height);

          // Add story points label on top of the bar
          ctx.fillStyle = '#000';
          ctx.font = '12px Arial';
          ctx.fillText(`${record.totalVelocity}`, x + barWidth / 2 - 10, y - 5);

          // Draw X-axis labels (Sprint numbers)
          ctx.fillStyle = '#000';
          ctx.font = '10px Arial';
          ctx.fillText(`${record.sprint}`, x + barWidth / 2 - 5, chartHeight - padding / 2);
        });

        // Draw Y-axis
        ctx.strokeStyle = '#000';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(padding, padding);
        ctx.lineTo(padding, chartHeight - padding);
        ctx.lineTo(chartWidth - padding, chartHeight - padding);
        ctx.stroke();
      }
    }
  }, [props.velocityHistory]);

  useEffect(() => {
    drawChart();
  }, [drawChart]);

  return (
    <div className="popup-overlay">
      <div className={`customer-feedback-popup-modal`}>
        <h3>📈 Velocity Report</h3>
        
        {/* Chart */}
        {props.velocityHistory.length > 0 && (
          <div style={{ marginBottom: '20px', textAlign: 'center' }}>
            <canvas
              ref={chartRef}
              width={Math.min(800, props.velocityHistory.length * 60 + 80)}
              height={250}
              style={{
                backgroundColor: '#fff',
                border: '1px solid #ddd',
                borderRadius: '4px',
              }}
            />
          </div>
        )}

        {/* Table */}
        <div style={{ marginBottom: '10px', fontSize: '12px', display: 'flex', gap: '20px', justifyContent: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <div style={{ width: '20px', height: '20px', backgroundColor: '#e8f5e9', border: '1px solid #ccc' }}></div>
            <span>Currently Employed</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <div style={{ width: '20px', height: '20px', backgroundColor: '#ffebee', border: '1px solid #ccc', opacity: 0.7 }}></div>
            <span>Not Currently Employed</span>
          </div>
        </div>
        <table className="feedback-table">
          <thead>
            <tr>
              <th>Sprint</th>
              <th>Total Velocity</th>
              {allDevelopers.map(([devId, devInfo]) => {
                const isCurrentlyEmployed = props.team.find(m => m.id === devId)?.currentlyEmployed ?? false;
                return (
                  <th 
                    key={devId} 
                    colSpan={2}
                    style={{
                      backgroundColor: isCurrentlyEmployed ? '#e8f5e9' : '#ffebee',
                      fontWeight: isCurrentlyEmployed ? 'bold' : 'normal',
                      opacity: isCurrentlyEmployed ? 1 : 0.7
                    }}
                    title={isCurrentlyEmployed ? 'Currently Employed' : 'Not Currently Employed'}
                  >
                    {devInfo.icon} {devInfo.name}
                  </th>
                );
              })}
            </tr>
            {allDevelopers.length > 0 && (
              <tr>
                <th></th>
                <th></th>
                {allDevelopers.map(([devId, devInfo]) => {
                  const isCurrentlyEmployed = props.team.find(m => m.id === devId)?.currentlyEmployed ?? false;
                  return (
                    <React.Fragment key={devId}>
                      <th 
                        style={{ 
                          fontSize: '11px',
                          backgroundColor: isCurrentlyEmployed ? '#e8f5e9' : '#ffebee',
                          opacity: isCurrentlyEmployed ? 1 : 0.7
                        }}
                      >
                        Code
                      </th>
                      <th 
                        style={{ 
                          fontSize: '11px',
                          backgroundColor: isCurrentlyEmployed ? '#e8f5e9' : '#ffebee',
                          opacity: isCurrentlyEmployed ? 1 : 0.7
                        }}
                      >
                        QA
                      </th>
                    </React.Fragment>
                  );
                })}
              </tr>
            )}
          </thead>
          <tbody>
            {props.velocityHistory.map((record, index) => {
              return (
                <tr key={index}>
                  <td>{record.sprint}</td>
                  <td style={{ textAlign: 'center', fontWeight: 'bold' }}>{record.totalVelocity}</td>
                  {allDevelopers.map(([devId]) => {
                    const devData = record.developerVelocity[devId];
                    if (devData) {
                      // Developer worked in this sprint
                      return (
                        <React.Fragment key={devId}>
                          <td style={{ textAlign: 'center' }}>{devData.code}</td>
                          <td style={{ textAlign: 'center' }}>{devData.qa}</td>
                        </React.Fragment>
                      );
                    } else {
                      // Developer didn't work in this sprint - show empty cells
                      return (
                        <React.Fragment key={devId}>
                          <td style={{ textAlign: 'center' }}>-</td>
                          <td style={{ textAlign: 'center' }}>-</td>
                        </React.Fragment>
                      );
                    }
                  })}
                </tr>
              )
            })}
          </tbody>
        </table>
        <button className="close-button" onClick={props.closeVelocityReportCallback}>Close</button>
      </div>
    </div>
  );
}
